'use client'

import { useResolveLocaleField } from '@/app/[locale]/_hooks/useResolveLocaleField'
import { IRedux } from '@/app/[locale]/_store/types'
import { ICategory } from '@/app/[locale]/_types'
import { Button } from '@/components/ui/button'
import { Box, HStack } from '@chakra-ui/react'
import classNames from 'classnames'
import Link from 'next/link'
import React, { RefObject, useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import styles from './styles.module.css'
import { useActiveCategory } from './useActiveCategory'
import { useCategoryOffsets } from './useCategoryOffsets'
import { useScrollPosition } from './useScrollPosition'
import { getHeaderLogoConfig, getStickyHeaderOffset } from '@/app/[locale]/_utils/getHeaderLogoConfig'

interface IProps {
  data: ICategory[]
  instanceRef: RefObject<any>
  isInteractive?: boolean
  isSeparatePage?: boolean
}

function FixedBar({
  isInteractive = false,
  data,
  instanceRef,
  isSeparatePage,
}: IProps) {
  const [isManualScroll, setIsManualScroll] = useState(false)

  const resolveLocale = useResolveLocaleField()
  const catalogMenuRef = useRef<HTMLDivElement>(null)
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const settings = useSelector((state: IRedux) => state.settings)
  const { is_hall } = useSelector((state: IRedux) => state.session)

  // Get category offsets
  const categoriesOffsetTops = useCategoryOffsets(data)

  // Handle scroll position
  const scrollPosition = useScrollPosition({
    isManualScroll,
    onScrollChange: () => {}, // No additional action needed
    onManualScrollEnd: () => setIsManualScroll(false),
  })

  // Handle active category and fixed catalog state
  const {
    isFixedCatalog,
    activeCategory,
    setActiveCategoryManual,
    scrollToCategory,
  } = useActiveCategory({
    data,
    categoriesOffsetTops,
    scrollPosition,
    isManualScroll,
    instanceRef,
  })

  const headerOffsetMobile = useMemo(() => {
    const logoConfig = getHeaderLogoConfig(settings?.data?.data)
    return getStickyHeaderOffset({
      addressPlacement: logoConfig.addressPlacement,
      isCompactLayout: logoConfig.isCompactLayout,
      isHall: is_hall,
      isInteractive,
    })
  }, [settings?.data?.data, is_hall, isInteractive])

  // Handle horizontal scroll of the catalog menu when activeCategory changes
  const scrollToActiveCategory = useCallback(() => {
    if (
      !catalogMenuRef.current ||
      !activeCategory ||
      !buttonRefs.current.has(activeCategory)
    )
      return

    const targetElement = buttonRefs.current.get(activeCategory)
    if (!targetElement) return

    const containerWidth = catalogMenuRef.current.offsetWidth
    const elementWidth = targetElement.offsetWidth
    const elementLeft = targetElement.offsetLeft
    const scrollLeft = elementLeft - containerWidth / 2 + elementWidth / 2

    catalogMenuRef.current.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    })
  }, [activeCategory])

  // Trigger scroll when active category changes
  React.useEffect(() => {
    scrollToActiveCategory()
  }, [scrollToActiveCategory])

  const handleCategoryClick = useCallback(
    (slug: string) => {
      setIsManualScroll(true)
      setActiveCategoryManual(slug)
      scrollToCategory(slug)
    },
    [setActiveCategoryManual, scrollToCategory]
  )

  const categoryButtons = useMemo(() => {
    return data?.map((category, idx) => {
      // For separate pages, use Next.js Link
      if (isSeparatePage) {
        return (
          <Button
            key={`${category.id}-${category.slug}-${idx}`}
            ref={(el) => {
              if (el) buttonRefs.current.set(category.slug, el)
            }}
            variant='ghost'
            size={{ base: 'xs', lg: 'sm' }}
            color={category.slug === activeCategory ? 'text' : 'text.muted'}
            bg={
              category.slug === activeCategory
                ? 'background'
                : 'background.card'
            }
            aria-current={category.slug === activeCategory ? 'true' : undefined}
            asChild
          >
            <Link href={`/category/${category?.slug}`}>
              {resolveLocale(category?.title)}
            </Link>
          </Button>
        )
      }

      // For hash navigation, use native <a> tag
      return (
        <Button
          key={`${category.id}-${category.slug}-${idx}`}
          ref={(el) => {
            if (el) buttonRefs.current.set(category.slug, el)
          }}
          variant={category.slug === activeCategory ? 'solid' : 'ghost'}
          size={{ base: 'xs', lg: 'sm' }}
          color={category.slug === activeCategory ? 'text' : 'text.muted'}
          bg={
            category.slug === activeCategory ? 'background' : 'background.card'
          }
          onClick={() => handleCategoryClick(category.slug)}
          aria-current={category.slug === activeCategory ? 'true' : undefined}
        >
          {resolveLocale(category?.title)}
        </Button>
      )
    })
  }, [data, activeCategory, resolveLocale, isSeparatePage, handleCategoryClick])

  return (
    <Box
      bg='background.card'
      className={classNames(styles.top, {
        [styles.active]: isFixedCatalog,
      })}
      transform={{
        base: `translateY(${isFixedCatalog ? headerOffsetMobile : '-100%'})`,
        lg: `translateY(${
          isFixedCatalog ? (isInteractive ? 0 : '150%') : '-100%'
        })`,
      }}
    >
      <Box pt={{ base: 2, lg: 3 }} pb={3}>
        <HStack
          gap={0}
          px={4}
          overflowX='auto'
          scrollbar='hidden'
          ref={catalogMenuRef}
          scrollBehavior='smooth'
          height={{ base: 'fit-content' }}
        >
          {categoryButtons}
        </HStack>
      </Box>
    </Box>
  )
}

export default React.memo(FixedBar)
